<script setup lang="ts">
/**
 * Bouton de résiliation (ou rétractation) d'un contrat + modale de confirmation.
 *
 * Composant partagé par la page « Mes contrats » et la page « Souscrire un
 * produit » pour que le comportement (délai légal de rétractation, appel back,
 * gestion d'erreur, copie i18n) vive à un seul endroit. Le parent recharge la
 * liste des contrats via l'événement `terminated`. Ne rend rien si le contrat
 * n'est pas actif (une résiliation déjà demandée n'est pas re-proposée).
 */
import { ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { requestTerminating } from '@/services/account'
import { ApiError } from '@/services/http'
import type { MemberContract } from '@/data/seedMember'
import ConfirmDialog from '@/components/ui/ConfirmDialog.vue'

const props = withDefaults(
  defineProps<{
    contract: MemberContract
    /** Style du déclencheur : 'solid' (page contrats) ou 'pill' (cartes produit). */
    variant?: 'solid' | 'pill'
  }>(),
  { variant: 'solid' },
)
const emit = defineEmits<{ terminated: [] }>()

const { t } = useI18n()

const confirmOpen = ref(false)
const terminating = ref(false)
const actionError = ref<string | null>(null)

/** Délai légal de rétractation (aligné sur le back RequestContractTermination). */
const RETRACTION_DAYS = 14
/** Vrai si le contrat est encore dans le délai de rétractation de 14 jours
 *  (basé sur la date de souscription, comme le back). */
const isRetraction = computed(() => {
  const iso = props.contract.subscribedOn
  if (!iso) return false
  const limit = new Date(iso)
  if (Number.isNaN(limit.getTime())) return false
  limit.setDate(limit.getDate() + RETRACTION_DAYS)
  limit.setHours(23, 59, 59, 999)
  return new Date() <= limit
})

/** Demande la résiliation (ou rétractation), puis notifie le parent (reload). */
async function terminate() {
  if (terminating.value) return
  actionError.value = null
  terminating.value = true
  try {
    await requestTerminating(props.contract.reference)
    confirmOpen.value = false
    emit('terminated')
  } catch (err) {
    confirmOpen.value = false
    actionError.value =
      err instanceof ApiError && err.status === 422
        ? t('auth.account.contracts.terminateError')
        : t('auth.account.contracts.actionError')
  } finally {
    terminating.value = false
  }
}
</script>

<template>
  <template v-if="contract.status === 'active'">
    <button
      type="button"
      :class="variant === 'pill' ? 'term-btn term-btn--pill' : 'term-btn term-btn--solid'"
      @click="confirmOpen = true"
    >
      {{ isRetraction ? t('auth.account.contracts.retract') : t('auth.account.contracts.terminate') }}
    </button>
    <p v-if="actionError" class="term-btn__error" role="alert">{{ actionError }}</p>

    <ConfirmDialog
      :open="confirmOpen"
      :title="isRetraction ? t('auth.account.contracts.confirmRetractTitle') : t('auth.account.contracts.confirmTerminateTitle')"
      :message="isRetraction ? t('auth.account.contracts.confirmRetractBody') : t('auth.account.contracts.confirmTerminateBody')"
      :confirm-label="isRetraction ? t('auth.account.contracts.retract') : t('auth.account.contracts.terminate')"
      :cancel-label="t('auth.account.contracts.confirmCancel')"
      :loading="terminating"
      danger
      @confirm="terminate"
      @cancel="confirmOpen = false"
    />
  </template>
</template>

<style scoped>
/* Variante 'solid' : bouton principal de la page « Mes contrats ». */
.term-btn--solid {
  padding: 0.75rem 1.6rem;
  border: 0;
  border-radius: var(--radius-pill);
  background: var(--color-chocolate);
  color: var(--color-white);
  font-size: var(--text-sm);
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: background var(--dur-fast) var(--ease-out);
}
.term-btn--solid:hover { background: color-mix(in oklab, var(--color-chocolate) 85%, white); }

/* Variante 'pill' : action discrète dans les cartes de « Souscrire un produit ». */
.term-btn--pill {
  display: inline-flex;
  align-items: center;
  padding: 0.4rem 0.7rem;
  border: 1px solid color-mix(in oklab, var(--color-cherry) 35%, white);
  border-radius: var(--radius-pill);
  background: var(--color-white);
  font-size: var(--text-xs);
  font-weight: 600;
  color: var(--color-cherry);
  cursor: pointer;
  transition: border-color var(--dur-fast), color var(--dur-fast);
}
.term-btn--pill:hover { border-color: var(--color-cherry); color: var(--color-cherry); }

.term-btn__error {
  margin-top: 0.5rem;
  font-size: var(--text-sm);
  color: var(--color-cherry);
}
</style>
